import moment from 'moment';
import '@aha-app/moment-fquarter';
import './moment-business';
import { MONTH_NUMBERS } from './dates';
import { type RecordId } from '../../types';

const DATE_FORMAT = 'YYYY-MM-DD';

export const idToDate = (id: RecordId) => {
  // The ID in JS will lose the last 3 digits of precision, but those digits don't matter
  // since the last 3 bytes in the 64-bit number are random anyway.
  return moment(new Date((Number(id) / 4294967296) * 1000));
};

export const dateAdd = (
  date,
  count,
  { period = 'days', business = false } = {}
) => {
  const addFn = business ? 'businessAdd' : 'add';
  if (business && !['days', 'day'].includes(period)) {
    console.warn(
      `Invoked 'dateAdd' with period '${period}' and 'business', but 'business' adding only supports days.`
    );
  }
  return moment.utc(date)[addFn](count, period).format(DATE_FORMAT);
};

export function formatDateToLocale(date, fallbackFormat = DATE_FORMAT) {
  // Ensure the date is a moment object.
  date = moment.isMoment(date) ? date : moment(date);

  if (!date.isValid()) return date;

  const formattedDate = new Intl.DateTimeFormat(navigator?.language)?.format(
    // Strip all time/timezone info, so we don't have to worry about UTC offset.
    new Date(date.year(), date.month(), date.date())
  );

  return formattedDate ?? dateFormat(date, fallbackFormat);
}

export const dateDiff = (startDate, endDate, { business = false } = {}) => {
  startDate = moment.utc(startDate);
  endDate = moment.utc(endDate);
  const diffFn = business ? 'businessDiff' : 'diff';
  return endDate[diffFn](startDate, 'days');
};

export const dateIsSame = (dateA, dateB) =>
  moment.utc(dateA).isSame(moment.utc(dateB), 'day');

export const dateSubtract = (date, count, opts) => dateAdd(date, -count, opts);

export const dateMin = (...dates) =>
  moment
    .min(dates.filter(date => Boolean(date)).map(date => moment.utc(date)))
    .format(DATE_FORMAT);

export const dateStartOf = (date, span) =>
  moment.utc(date).startOf(span).format(DATE_FORMAT);

export const dateFormat = (date, format = DATE_FORMAT) =>
  moment.utc(date).format(format);

export const dateMonthIndex = date => moment.utc(date).month();

/**
 * Returns a date at the beginning of the given year and month.
 * @example
 * // returns '2021-11-01'
 * monthYearToDate(2021, 10);
 * @returns {string} Returns the date at the beginning of the month as a string.
 */
export const monthYearToDate = (year, month) => {
  return moment.utc([year, month]).format(DATE_FORMAT);
};

/**
 * Returns the date for the first day of the fiscal year as a string.
 * @params date - The date for which we want to know the start of the fiscal year.
 * @params [fiscalMonth=1] - The first month of the fiscal year (1-12).
 */
export const startOfFiscalYear = (
  date: moment.MomentInput,
  { fiscalMonth = MONTH_NUMBERS.JANUARY }: { fiscalMonth?: number } = {}
) => {
  let year = getYear(date);

  // months in JavaScript dates go from 0-11, but the fiscalMonth goes from 1-12 so we subtract 1 to the fiscal month
  const fiscalMonthIndex = fiscalMonth - 1;
  if (getMonthIndex(date) < fiscalMonthIndex) {
    year = year - 1;
  }

  return moment.utc([year, fiscalMonth - 1]).format(DATE_FORMAT);
};

/**
 * Returns the fiscal information for the given date and fiscal month.
 */
export const dateFiscal = (
  date,
  { fiscalMonth = MONTH_NUMBERS.JANUARY } = {}
) => {
  return moment.utc(date).fquarter(fiscalMonth);
};

export const dateIsBefore = (a, b) => moment.utc(a).isBefore(moment.utc(b));
export const dateIsSameOrBefore = (a, b) =>
  moment.utc(a).isSameOrBefore(moment.utc(b));

export const dateIsAfter = (a, b) => moment.utc(a).isAfter(moment.utc(b));
export const dateIsSameOrAfter = (a, b) =>
  moment.utc(a).isSameOrAfter(moment.utc(b));

export const dateIsWeekDay = date => {
  return moment.utc(date).isoWeekday() < 6;
};

export const dateIsWeekendDay = date => !dateIsWeekDay(date);

export const dateCurrentOrNextWeekDay = date => {
  if (dateIsWeekDay(date)) {
    return date;
  } else {
    // Saturday is 6, Sunday is 7
    date = moment.utc(date);
    return date.add(8 - date.isoWeekday(), 'days').format(DATE_FORMAT);
  }
};

export const dateCurrentOrPreviousWeekDay = date => {
  if (dateIsWeekDay(date)) {
    return date;
  } else {
    // Saturday is 6, Sunday is 7
    date = moment.utc(date);
    return date.subtract(date.isoWeekday() - 5, 'days').format(DATE_FORMAT);
  }
};

export const getYear = date => moment(date, DATE_FORMAT).year();
export const getMonthIndex = date => moment(date, DATE_FORMAT).month();
